import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, Users, Briefcase, DollarSign, 
  TrendingUp, FileText, LogOut, Menu, Shield, ChevronDown, ChevronRight,
  Building2, BarChart3, UserCog, Settings, Layers, Package, Cloud, Server, HardDrive,
  AlertTriangle, Grid3X3, FolderKanban, Contact, History, Mail, CalendarDays, GraduationCap, UserCircle, ListChecks, CircleDot, ClipboardList, Ruler, FolderTree, ScrollText, ShoppingCart, FileStack, Coins, Receipt, Sliders, SplitSquareHorizontal, Network,
  HeartPulse, Target, CalendarClock, FileCheck, Clock, Bell, Plug, Sparkles, Wrench, StopCircle, Eraser, Snowflake, BookUser
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ROLE_LABELS, ROUTE_TO_RESOURCE, checkPermission } from '@/utils/permissions';
import { ROUTE_PREFETCHERS } from '@/App';

// Trigger a route chunk load on hover. Webpack dedupes by module specifier,
// so by the time the user actually clicks the chunk is already in cache.
const prefetchRoute = (href) => {
  const loader = ROUTE_PREFETCHERS[href];
  if (loader) {
    // Fire and forget — failures will be surfaced again on actual nav.
    Promise.resolve(loader()).catch(() => {});
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Navigation configuration with resource mapping
const navigationSections = [
  {
    name: 'Dashboards',
    icon: BarChart3,
    items: [
      { name: 'System Overview', href: '/dashboards/overview', icon: LayoutDashboard, resource: 'system_overview' },
      { name: 'Cockpit Activity', href: '/dashboards/activity', icon: BarChart3, resource: 'cockpit_activity' },
      { name: 'Manpower Dashboard', href: '/dashboard', icon: LayoutDashboard, resource: 'dashboard' },
      { name: 'Cloud Dashboard', href: '/cloud/dashboard', icon: Cloud, resource: 'dashboard' },
    ]
  },
  {
    name: 'Company Masters',
    icon: Building2,
    items: [
      { name: 'Client Master', href: '/customers', icon: Briefcase, resource: 'customers' },
      { name: 'Contact Master', href: '/contacts', icon: Contact, resource: 'contacts' },
      { name: 'Vertical Master', href: '/verticals', icon: Layers, resource: 'vertical_master' },
      { name: 'Product Master', href: '/products', icon: Package, resource: 'product_master' },
      { name: 'Employee Master', href: '/employees', icon: Users, resource: 'employees' },
      { name: 'UoM', href: '/masters/uom', icon: Ruler, resource: 'uom_master' },
      { name: 'Item Group', href: '/masters/item-groups', icon: FolderTree, resource: 'item_group_master' },
      { name: 'Items', href: '/masters/items', icon: Package, resource: 'items_master' },
      { name: 'Currencies', href: '/masters/currencies', icon: Coins, resource: 'currency_master' },
      { name: 'Payment Splits', href: '/masters/payment-splits', icon: SplitSquareHorizontal, resource: 'payment_splits_master' },
    ]
  },
  {
    name: 'Cloud',
    icon: Cloud,
    items: [
      { name: 'Cloud Master', href: '/cloud/clouds', icon: Cloud, resource: 'cloud_master' },
      { name: 'Server Type Master', href: '/cloud/server-types', icon: Server, resource: 'server_type_master' },
      { name: 'Server Master', href: '/cloud/servers', icon: HardDrive, resource: 'server_master' },
      { name: 'Server Clusters', href: '/cloud/server-clusters', icon: Network, resource: 'server_clusters' },
      { name: 'Bulk Server Costs', href: '/cloud/server-costs', icon: DollarSign, resource: 'server_costs' },
      { name: 'Server Allocations', href: '/cloud/server-allocations', icon: FileText, resource: 'server_allocations' },
      { name: 'Manage Allocations', href: '/cloud/allocations/manage', icon: StopCircle, resource: 'server_allocations', action: 'edit' },
      { name: 'Cloud Revenues', href: '/cloud/revenues', icon: TrendingUp, resource: 'cloud_revenues' },
      { name: 'Manage Revenues', href: '/cloud/revenues/manage', icon: StopCircle, resource: 'cloud_revenues', action: 'edit' },
      { name: 'Client Report', href: '/cloud/reports/client', icon: FileText, resource: 'cloud_client_report' },
      { name: 'Product Report', href: '/cloud/reports/product', icon: FileText, resource: 'cloud_product_report' },
      { name: 'Server Report', href: '/cloud/reports/server', icon: FileText, resource: 'cloud_server_report' },
      { name: 'Unallocated Servers', href: '/cloud/reports/unallocated', icon: AlertTriangle, resource: 'cloud_server_report' },
      { name: 'Allocation History', href: '/cloud/reports/allocation-history', icon: History, resource: 'cloud_allocation_history' },
      { name: 'Revenue History', href: '/cloud/reports/revenue-history', icon: History, resource: 'cloud_revenue_history' },
    ]
  },
  {
    name: 'Manpower',
    icon: UserCog,
    items: [
      { name: 'Salaries', href: '/salaries', icon: DollarSign, resource: 'salaries' },
      { name: 'Bulk Salaries', href: '/salaries/bulk-edit', icon: DollarSign, resource: 'salaries' },
      { name: 'Manpower Revenues', href: '/revenues', icon: TrendingUp, resource: 'revenues' },
      { name: 'Bulk Revenues', href: '/revenues/bulk-edit', icon: TrendingUp, resource: 'revenues' },
      { name: 'Manage Revenues', href: '/revenues/manage', icon: StopCircle, resource: 'revenues', action: 'edit' },
      { name: 'Allocations', href: '/allocations', icon: FileText, resource: 'allocations' },
      { name: 'Bulk Allocations', href: '/allocations/bulk-edit', icon: FileText, resource: 'allocations' },
      { name: 'Manage Allocations', href: '/allocations/manage', icon: StopCircle, resource: 'allocations', action: 'edit' },
      { name: 'Manpower Report', href: '/reports/manpower', icon: FileText, resource: 'manpower_report' },
      { name: 'Client Report', href: '/reports/client', icon: FileText, resource: 'client_report' },
      { name: 'Product Report', href: '/reports/product', icon: Package, resource: 'manpower_report' },
      { name: 'Unallocated Report', href: '/reports/unallocated', icon: FileText, resource: 'unallocated_report' },
      { name: 'Managers Report', href: '/reports/managers', icon: FileText, resource: 'managers_report' },
      { name: 'Cost Breakdown', href: '/reports/cost-breakdown', icon: FileText, resource: 'cost_breakdown_report' },
      { name: 'Allocation History', href: '/reports/manpower-allocation-history', icon: History, resource: 'manpower_allocation_history' },
      { name: 'Revenue History', href: '/reports/manpower-revenue-history', icon: History, resource: 'manpower_revenue_history' },
    ]
  },
  {
    name: 'Project Management',
    icon: FolderKanban,
    items: [
      { name: 'Project Master', href: '/project/projects', icon: FolderKanban, resource: 'project_master' },
      { name: 'Task Type Master', href: '/project/task-types', icon: ListChecks, resource: 'task_type_master' },
      { name: 'Task Status Master', href: '/project/task-statuses', icon: CircleDot, resource: 'task_status_master' },
      { name: 'Project Plan', href: '/project/plan', icon: ClipboardList, resource: 'project_master' },
      { name: 'Client Meetings', href: '/project/meetings', icon: CalendarDays, resource: 'client_meetings' },
      { name: 'Meeting Intelligence', href: '/project/meeting-intelligence', icon: Sparkles, resource: 'client_meetings' },
      { name: 'Client Trainings', href: '/project/trainings', icon: GraduationCap, resource: 'client_trainings' },
      { name: 'Client Profile', href: '/project/client-profile', icon: UserCircle, resource: 'client_dashboard' },
      { name: 'Client Dashboard', href: '/project/client-dashboard', icon: BarChart3, resource: 'client_dashboard' },
      { name: 'Contact Coverage', href: '/project/contact-coverage', icon: Users, resource: 'client_contact_dashboard' },
      { name: 'Project Reviews', href: '/project/reviews', icon: ClipboardList, resource: 'project_master' },
      { name: 'Review Dashboard', href: '/project/review-dashboard', icon: BarChart3, resource: 'review_dashboard' },
      { name: 'Module Utilization', href: '/project/module-utilization', icon: Grid3X3, resource: 'module_utilization' },
      { name: 'Module Util. Report', href: '/project/module-utilization-report', icon: BarChart3, resource: 'module_utilization' },
    ]
  },
  {
    name: 'Commercial',
    icon: ScrollText,
    items: [
      { name: 'Dashboard', href: '/commercial/dashboard', icon: BarChart3, resource: 'commercial_dashboard' },
      { name: 'Proposals', href: '/commercial/proposals', icon: FileStack, resource: 'commercial_proposals' },
      { name: 'Contracts', href: '/commercial/contracts', icon: ScrollText, resource: 'commercial_contracts' },
      { name: 'Purchase Orders', href: '/commercial/purchase-orders', icon: ShoppingCart, resource: 'commercial_purchase_orders' },
      { name: 'Invoices', href: '/commercial/invoices', icon: Receipt, resource: 'commercial_invoices' },
      { name: 'Pricing Rules', href: '/commercial/pricing-rules', icon: Sliders, resource: 'commercial_pricing_rules' },
    ]
  },
  {
    name: 'Reports',
    icon: BarChart3,
    items: [
      { name: 'Advanced Reports (All)', href: '/commercial/reports', icon: BarChart3, resource: 'advanced_reports' },
      { name: 'Revenue', href: '/reports/revenue', icon: DollarSign, resource: 'advanced_reports' },
      { name: 'Win Rate', href: '/reports/win-rate', icon: TrendingUp, resource: 'advanced_reports' },
      { name: 'Invoice Aging', href: '/reports/invoice-aging', icon: Clock, resource: 'advanced_reports' },
      { name: 'PO Utilization', href: '/reports/po-utilization', icon: FileCheck, resource: 'advanced_reports' },
      { name: 'Expiry Calendar', href: '/reports/expiry-calendar', icon: CalendarClock, resource: 'advanced_reports' },
      { name: 'Profitability', href: '/reports/profitability', icon: BarChart3, resource: 'advanced_reports' },
      { name: 'Emp Utilization', href: '/reports/employee-utilization', icon: Users, resource: 'advanced_reports' },
      { name: 'Project Health', href: '/reports/project-health', icon: HeartPulse, resource: 'advanced_reports' },
      { name: 'Contact Coverage', href: '/reports/contact-coverage', icon: Target, resource: 'contact_coverage_report' },
      { name: 'Client Engagement', href: '/reports/client-engagement', icon: Snowflake, resource: 'client_engagement_report' },
      { name: 'Contact List', href: '/reports/contact-list', icon: BookUser, resource: 'contact_list_report' },
      { name: 'Server Cost', href: '/reports/server-cost', icon: Server, resource: 'advanced_reports' },
      { name: 'Cloud Spend', href: '/reports/cloud-spend', icon: Cloud, resource: 'advanced_reports' },
    ]
  },
  {
    name: 'Configuration',
    icon: Settings,
    items: [
      { name: 'User Master', href: '/users', icon: Shield, resource: 'user_management' },
      { name: 'Role Master', href: '/roles', icon: UserCog, resource: 'role_management' },
      { name: 'Company Settings', href: '/company-settings', icon: Building2, adminOnly: true },
      { name: 'Integrations', href: '/integrations', icon: Plug, resource: 'integrations' },
      { name: 'Notification Engine', href: '/notification-settings', icon: Bell, resource: 'notification_settings' },
      { name: 'Audit Logs', href: '/audit-logs', icon: History, resource: 'audit_logs' },
      { name: 'Maintenance', href: '/maintenance', icon: Wrench, adminOnly: true },
      { name: 'Data Hygiene', href: '/data-hygiene', icon: Eraser, resource: 'data_hygiene' },
    ]
  },
];

const NavSection = ({ section, isActive, onToggle, location, onItemClick, permissions, isAdmin, hygieneIssueCount }) => {
  const SectionIcon = section.icon;
  
  // Filter items based on view permission
  const visibleItems = section.items.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    if (isAdmin) return true;
    if (!item.resource) return true;
    return checkPermission(permissions, item.resource, item.action || 'view');
  });
  
  // Hide entire section if no items are visible
  if (visibleItems.length === 0) return null;
  
  const hasActiveItem = visibleItems.some(item => location.pathname === item.href);
  const isOpen = isActive || hasActiveItem;
  
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold rounded-lg transition-all ${
          hasActiveItem
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground'
        }`}
      >
        <div className="flex items-center">
          <SectionIcon className="mr-3 h-4 w-4" />
          {section.name}
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>
      
      {isOpen && (
        <div className="mt-1 ml-4 space-y-0.5">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isItemActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onItemClick}
                onMouseEnter={() => prefetchRoute(item.href)}
                onFocus={() => prefetchRoute(item.href)}
                data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  isItemActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon className="mr-3 h-4 w-4" />
                {item.name}
                {item.href === '/data-hygiene' && hygieneIssueCount > 0 && (
                  <span
                    className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
                    data-testid="nav-data-hygiene-badge"
                  >
                    {hygieneIssueCount > 99 ? '99+' : hygieneIssueCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Layout = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState({
    'Dashboards': true,
    'Company Masters': false,
    'Manpower': false,
  });
  const [permissions, setPermissions] = useState(null);
  const [hygieneIssueCount, setHygieneIssueCount] = useState(0);
  
  const isAdmin = user?.role === 'admin';
  
  // Load role permissions
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user?.role) return;
      
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/roles/${user.role}/permissions`,
          { withCredentials: true }
        );
        setPermissions(response.data);
      } catch (error) {
        console.error('Error loading permissions:', error);
        // If admin, set full permissions
        if (isAdmin) {
          setPermissions(null); // Admin bypasses permissions check
        }
      }
    };
    
    loadPermissions();
  }, [user?.role, isAdmin]);

  // Data Hygiene badge — lets duplicate/orphan records that creep back in
  // via CSV imports surface without needing to remember to check the page.
  useEffect(() => {
    if (!isAdmin && !checkPermission(permissions, 'data_hygiene', 'view')) return;
    axios.get(`${BACKEND_URL}/api/data-hygiene/summary`, { withCredentials: true })
      .then((res) => setHygieneIssueCount(res.data.total_issues || 0))
      .catch(() => {});
  }, [isAdmin, permissions]);
  
  const toggleSection = (sectionName) => {
    setOpenSections(prev => {
      // Close all other sections, toggle the clicked one
      const newState = {};
      Object.keys(prev).forEach(key => { newState[key] = false; });
      newState[sectionName] = !prev[sectionName];
      return newState;
    });
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };
  
  const renderNavigation = (onItemClick = null) => (
    <>
      {navigationSections.map((section) => (
        <NavSection
          key={section.name}
          section={section}
          isActive={openSections[section.name]}
          onToggle={() => toggleSection(section.name)}
          location={location}
          onItemClick={onItemClick}
          permissions={permissions}
          isAdmin={isAdmin}
          hygieneIssueCount={hygieneIssueCount}
        />
      ))}
    </>
  );
  
  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 border-r border-border bg-card">
        <div className="flex-1 flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-border">
            <h1 className="text-2xl font-bold text-primary">Eazy Cockpit</h1>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {renderNavigation()}
          </nav>
          
          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-3 mb-2">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                  {user?.name?.[0] || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-3 px-1">
              Role: <span className="font-semibold text-primary">{ROLE_LABELS[user?.role] || user?.role}</span>
            </p>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setSidebarOpen(false)}>
          <div className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="h-16 flex items-center px-6 border-b border-border">
              <h1 className="text-2xl font-bold text-primary">Eazy Cockpit</h1>
            </div>
            <nav className="px-4 py-6 space-y-1">
              {renderNavigation(() => setSidebarOpen(false))}
            </nav>
            
            {/* Mobile user section */}
            <div className="p-4 border-t border-border">
              <div className="flex items-center space-x-3 mb-2">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    {user?.name?.[0] || 'U'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-3 px-1">
                Role: <span className="font-semibold text-primary">{ROLE_LABELS[user?.role] || user?.role}</span>
              </p>
              <Button
                data-testid="logout-button"
                onClick={() => {
                  setSidebarOpen(false);
                  handleLogout();
                }}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <header className="lg:hidden h-16 border-b border-border bg-card flex items-center px-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>
          <h1 className="ml-4 text-xl font-bold text-primary">Eazy Cockpit</h1>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet context={{ user, permissions }} />
        </main>
      </div>
    </div>
  );
};

export default Layout;
